import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Tag, 
  Percent, 
  IndianRupee,
  Calendar,
  Users,
  BarChart3,
  Power,
  PowerOff,
  X,
  Check,
  Copy,
  TrendingUp,
  Clock,
  Building2,
  Gamepad2,
  AlertCircle
} from 'lucide-react';
import { useMasterData } from '../contexts/MasterDataContext';

import { API } from '../utils/apiConfig';

export default function CouponManagementPage() {
  const { venues, allGames: games } = useMasterData();
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterActive, setFilterActive] = useState(null); // null = all, true = active, false = inactive
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    max_discount_amount: '',
    min_booking_amount: '',
    valid_from: '',
    valid_till: '',
    usage_limit: '',
    usage_per_user: '1',
    is_active: true,
    applicable_venues: [],
    applicable_games: [],
    first_time_user_only: false
  });

  useEffect(() => {
    fetchCoupons();
    fetchStats();
  }, [filterActive]);

  const fetchCoupons = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/coupons`;
      if (filterActive !== null) {
        url += `?is_active=${filterActive}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoupons(response.data.coupons || []);
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/coupons/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();
    
    if (!formData.code.trim()) {
      toast.error('Coupon code is required');
      return;
    }
    if (!formData.discount_value) {
      toast.error('Discount value is required');
      return;
    }
    if (!formData.valid_from || !formData.valid_till) {
      toast.error('Valid from and valid till dates are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        code: formData.code.toUpperCase(),
        discount_value: parseFloat(formData.discount_value),
        max_discount_amount: formData.max_discount_amount ? parseFloat(formData.max_discount_amount) : null,
        min_booking_amount: formData.min_booking_amount ? parseFloat(formData.min_booking_amount) : 0,
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
        usage_per_user: parseInt(formData.usage_per_user) || 1,
        applicable_venues: formData.applicable_venues.length > 0 ? formData.applicable_venues : null,
        applicable_games: formData.applicable_games.length > 0 ? formData.applicable_games : null
      };

      if (editingCoupon) {
        await axios.put(`${API}/coupons/${editingCoupon.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Coupon updated successfully');
      } else {
        await axios.post(`${API}/coupons`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Coupon created successfully');
      }

      setShowModal(false);
      resetForm();
      fetchCoupons();
      fetchStats();
    } catch (error) {
      console.error('Failed to save coupon:', error);
      toast.error(error.response?.data?.detail || 'Failed to save coupon');
    }
  };

  const handleToggle = async (coupon) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/coupons/${coupon.id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Coupon ${coupon.is_active ? 'deactivated' : 'activated'}`);
      fetchCoupons();
      fetchStats();
    } catch (error) {
      toast.error('Failed to toggle coupon status');
    }
  };

  const handleDelete = async (coupon) => {
    if (!window.confirm(`Are you sure you want to delete coupon "${coupon.code}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/coupons/${coupon.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Coupon deleted');
      fetchCoupons();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      description: coupon.description || '',
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value.toString(),
      max_discount_amount: coupon.max_discount_amount?.toString() || '',
      min_booking_amount: coupon.min_booking_amount?.toString() || '',
      valid_from: coupon.valid_from?.split('T')[0] || '',
      valid_till: coupon.valid_till?.split('T')[0] || '',
      usage_limit: coupon.usage_limit?.toString() || '',
      usage_per_user: coupon.usage_per_user?.toString() || '1',
      is_active: coupon.is_active,
      applicable_venues: coupon.applicable_venues || [],
      applicable_games: coupon.applicable_games || [],
      first_time_user_only: coupon.first_time_user_only || false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingCoupon(null);
    setFormData({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: '',
      max_discount_amount: '',
      min_booking_amount: '',
      valid_from: '',
      valid_till: '',
      usage_limit: '',
      usage_per_user: '1',
      is_active: true,
      applicable_venues: [],
      applicable_games: [],
      first_time_user_only: false
    });
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Coupon code copied!');
  };

  const couponLink = (code) => `${window.location.origin}/?discount=${encodeURIComponent(code)}`;

  const copyLink = (code) => {
    navigator.clipboard.writeText(couponLink(code));
    toast.success('Auto-apply link copied!');
  };

  const filteredCoupons = coupons.filter(coupon => 
    coupon.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    coupon.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isExpired = (validTill) => {
    if (!validTill) return false;
    return new Date(validTill) < new Date();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  return (
    <DashboardLayout>
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Coupon Management</h1>
          <p className="text-slate-600 mt-1">Create and manage discount coupons</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-primary hover:bg-primary-hover"
          data-testid="create-coupon-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Coupon
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <Tag className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total_coupons}</p>
                <p className="text-xs text-slate-500">Total Coupons</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Power className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.active_coupons}</p>
                <p className="text-xs text-slate-500">Active</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total_usages}</p>
                <p className="text-xs text-slate-500">Total Uses</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <IndianRupee className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">₹{stats.total_discount_given?.toLocaleString()}</p>
                <p className="text-xs text-slate-500">Discount Given</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="p-4 mb-6 border-slate-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search coupons..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={filterActive === null ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterActive(null)}
            >
              All
            </Button>
            <Button
              variant={filterActive === true ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterActive(true)}
            >
              Active
            </Button>
            <Button
              variant={filterActive === false ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterActive(false)}
            >
              Inactive
            </Button>
          </div>
        </div>
      </Card>

      {/* Coupons List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
          <p className="text-slate-500 mt-2">Loading coupons...</p>
        </div>
      ) : filteredCoupons.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">No Coupons Found</h3>
          <p className="text-slate-500 mt-1">Create your first coupon to get started</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredCoupons.map((coupon) => (
            <Card 
              key={coupon.id} 
              className={`p-4 border-slate-200 ${!coupon.is_active ? 'opacity-60' : ''} ${isExpired(coupon.valid_till) ? 'border-red-200 bg-red-50/30' : ''}`}
              data-testid={`coupon-card-${coupon.code}`}
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                {/* Coupon Code & Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-mono font-bold text-lg text-violet-600 bg-violet-50 px-3 py-1 rounded-lg">
                      {coupon.code}
                    </span>
                    <button 
                      onClick={() => copyCode(coupon.code)}
                      className="p-1 hover:bg-slate-100 rounded"
                      title="Copy code"
                    >
                      <Copy className="w-4 h-4 text-slate-400" />
                    </button>
                    {!coupon.is_active && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">Inactive</span>
                    )}
                    {isExpired(coupon.valid_till) && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600">Expired</span>
                    )}
                    {coupon.first_time_user_only && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-600">First-time only</span>
                    )}
                  </div>
                  {coupon.description && (
                    <p className="text-sm text-slate-600 mb-2">{coupon.description}</p>
                  )}
                  <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      {coupon.discount_type === 'percentage' ? (
                        <><Percent className="w-3 h-3" /> {coupon.discount_value}% off</>
                      ) : (
                        <><IndianRupee className="w-3 h-3" /> ₹{coupon.discount_value} off</>
                      )}
                      {coupon.max_discount_amount && (
                        <span className="text-slate-400">(max ₹{coupon.max_discount_amount})</span>
                      )}
                    </span>
                    {coupon.min_booking_amount > 0 && (
                      <span className="flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Min ₹{coupon.min_booking_amount}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {formatDate(coupon.valid_from)} - {formatDate(coupon.valid_till)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" /> {coupon.used_count || 0}{coupon.usage_limit ? `/${coupon.usage_limit}` : ''} uses
                    </span>
                  </div>

                  {/* Shareable auto-apply link */}
                  <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`coupon-link-row-${coupon.code}`}>
                    <span className="text-[11px] font-medium text-slate-500 shrink-0">Auto-apply link:</span>
                    <code className="text-xs text-violet-700 bg-violet-50 px-2 py-1 rounded border border-violet-100 break-all">
                      {couponLink(coupon.code)}
                    </code>
                    <button
                      onClick={() => copyLink(coupon.code)}
                      className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-violet-600"
                      title="Copy auto-apply link"
                      data-testid={`copy-coupon-link-${coupon.code}`}
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggle(coupon)}
                    className={coupon.is_active ? 'text-amber-600 hover:bg-amber-50' : 'text-emerald-600 hover:bg-emerald-50'}
                  >
                    {coupon.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(coupon)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(coupon)}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">
                {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
              </h2>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <form onSubmit={handleCreateOrUpdate} className="p-4 space-y-4">
              {/* Code & Description */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Coupon Code *</label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    placeholder="e.g., MAKS100"
                    className="font-mono uppercase"
                    data-testid="coupon-code-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="e.g., Get 10% off on your first booking"
                  />
                </div>
              </div>

              {/* Discount Type & Value */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Discount Type *</label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({...formData, discount_type: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="flat">Flat Amount (₹)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Discount Value * {formData.discount_type === 'percentage' ? '(%)' : '(₹)'}
                  </label>
                  <Input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({...formData, discount_value: e.target.value})}
                    placeholder={formData.discount_type === 'percentage' ? '10' : '100'}
                    min="0"
                    max={formData.discount_type === 'percentage' ? '100' : undefined}
                    data-testid="discount-value-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Max Discount (₹)</label>
                  <Input
                    type="number"
                    value={formData.max_discount_amount}
                    onChange={(e) => setFormData({...formData, max_discount_amount: e.target.value})}
                    placeholder="e.g., 500"
                    min="0"
                  />
                  <p className="text-xs text-slate-400 mt-1">Cap for percentage discounts</p>
                </div>
              </div>

              {/* Min Amount & Validity */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Min Booking Amount (₹)</label>
                  <Input
                    type="number"
                    value={formData.min_booking_amount}
                    onChange={(e) => setFormData({...formData, min_booking_amount: e.target.value})}
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Valid From *</label>
                  <Input
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({...formData, valid_from: e.target.value})}
                    data-testid="valid-from-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Valid Till *</label>
                  <Input
                    type="date"
                    value={formData.valid_till}
                    onChange={(e) => setFormData({...formData, valid_till: e.target.value})}
                    data-testid="valid-till-input"
                  />
                </div>
              </div>

              {/* Usage Limits */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Total Usage Limit</label>
                  <Input
                    type="number"
                    value={formData.usage_limit}
                    onChange={(e) => setFormData({...formData, usage_limit: e.target.value})}
                    placeholder="Unlimited"
                    min="1"
                  />
                  <p className="text-xs text-slate-400 mt-1">Leave empty for unlimited</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Usage Per User</label>
                  <Input
                    type="number"
                    value={formData.usage_per_user}
                    onChange={(e) => setFormData({...formData, usage_per_user: e.target.value})}
                    placeholder="1"
                    min="1"
                  />
                </div>
              </div>

              {/* Applicable Venues */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  <Building2 className="w-4 h-4 inline mr-1" />
                  Applicable Venues
                </label>
                <div className="flex flex-wrap gap-2 p-3 border border-slate-200 rounded-lg bg-slate-50">
                  {(venues || []).map(venue => (
                    <label key={venue.id} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.applicable_venues.includes(venue.id)}
                        onChange={(e) => {
                          const newVenues = e.target.checked
                            ? [...formData.applicable_venues, venue.id]
                            : formData.applicable_venues.filter(v => v !== venue.id);
                          setFormData({...formData, applicable_venues: newVenues});
                        }}
                        className="rounded border-slate-300"
                      />
                      {venue.name}
                    </label>
                  ))}
                  {(!venues || venues.length === 0) && <span className="text-slate-400 text-sm">No venues available</span>}
                </div>
                <p className="text-xs text-slate-400 mt-1">Leave unchecked for all venues</p>
              </div>

              {/* Applicable Games */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  <Gamepad2 className="w-4 h-4 inline mr-1" />
                  Applicable Games
                </label>
                <div className="flex flex-wrap gap-2 p-3 border border-slate-200 rounded-lg bg-slate-50">
                  {(games || []).map(game => (
                    <label key={game} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.applicable_games.includes(game)}
                        onChange={(e) => {
                          const newGames = e.target.checked
                            ? [...formData.applicable_games, game]
                            : formData.applicable_games.filter(g => g !== game);
                          setFormData({...formData, applicable_games: newGames});
                        }}
                        className="rounded border-slate-300"
                      />
                      {game}
                    </label>
                  ))}
                  {(!games || games.length === 0) && <span className="text-slate-400 text-sm">No games available</span>}
                </div>
                <p className="text-xs text-slate-400 mt-1">Leave unchecked for all games</p>
              </div>

              {/* Options */}
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="rounded border-slate-300"
                  />
                  <span className="text-sm text-slate-700">Active</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.first_time_user_only}
                    onChange={(e) => setFormData({...formData, first_time_user_only: e.target.checked})}
                    className="rounded border-slate-300"
                  />
                  <span className="text-sm text-slate-700">First-time users only</span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="save-coupon-btn">
                  <Check className="w-4 h-4 mr-2" />
                  {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
    </DashboardLayout>
  );
}
